import React from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'; // Import Lucide React icons

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const pageSizes = [10, 20, 50];

  // Calculate the start and end page numbers to display
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, currentPage + 2);

  if (endPage - startPage < maxPagesToShow - 1) {
    if (currentPage < Math.ceil(maxPagesToShow / 2)) {
      endPage = Math.min(totalPages, maxPagesToShow);
    } else if (currentPage > totalPages - Math.floor(maxPagesToShow / 2)) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex justify-between items-center py-2">
      {/* Page Size Selector */}
      <div>
        <label className="mr-2 text-sm text-gray-600 dark:text-gray-300">Page Size:</label>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-3 py-2 border rounded-md text-sm text-gray-600 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
        >
          {pageSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center text-sm">
        <button
          className={`px-3 py-1 rounded mr-1 ${
            currentPage === 1
              ? 'text-gray-700 dark:text-gray-300 cursor-not-allowed'
              : 'text-black dark:text-gray-300'
          }`}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="h-5 w-5" /> {/* First Page */}
        </button>
        <button
          className={`px-3 py-1 rounded mr-1 ${
            currentPage === 1
              ? 'text-seondary dark:text-gray-300 cursor-not-allowed'
              : 'text-seondary dark:text-gray-300'
          }`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-5 w-5" /> {/* Previous Page */}
        </button>

        {pages.map((page) => (
          <button
            key={page}
            className={`px-3 py-1 mx-1 rounded ${
              currentPage === page
                ? 'bg-gray-700 dark:bg-gray-900 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}

        <button
          className={`px-3 py-1 rounded ml-1 ${
            currentPage === totalPages
              ? 'text-gray-700 dark:text-gray-300 cursor-not-allowed'
              : 'text-black dark:text-gray-300'
          }`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="h-5 w-5" /> {/* Next Page */}
        </button>
        <button
          className={`px-3 py-1 rounded ml-1 ${
            currentPage === totalPages
              ? 'text-gray-700 dark:text-gray-300 cursor-not-allowed'
              : 'text-black dark:text-gray-300'
          }`}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="h-5 w-5" /> {/* Last Page */}
        </button>
      </div>
    </div>
  );
};

export default Pagination;
